import { useState } from 'react';
import Sidebar from '../dashboard/components/Sidebar';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    companyName: 'BuildPro Construction',
    businessType: 'General Contractor',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    language: 'English',
    
    // Project Settings
    defaultProjectDuration: '90',
    autoAssignTasks: true,
    requireApproval: true,
    enableTimeTracking: true,
    projectNumberFormat: 'BP-{YYYY}-{###}',
    
    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyReports: true,
    projectUpdates: true,
    deadlineReminders: true,
    
    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: '30',
    passwordExpiry: '90',
    loginNotifications: true,
    
    // Integration Settings
    googleCalendar: false,
    quickbooks: false,
    dropbox: false,
    slack: false
  });

  const handleSave = () => {
    setIsEditing(false);
    // Save settings logic here
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original values
  };

  const tabs = [
    { id: 'general', name: 'General', icon: 'ri-settings-3-line' },
    { id: 'projects', name: 'Projects', icon: 'ri-folder-line' },
    { id: 'notifications', name: 'Notifications', icon: 'ri-notification-line' },
    { id: 'security', name: 'Security', icon: 'ri-shield-line' },
    { id: 'integrations', name: 'Integrations', icon: 'ri-links-line' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your application preferences and configurations</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 whitespace-nowrap"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Edit Settings
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Settings Navigation */}
          <div className="w-64 bg-white border-r border-gray-200 p-6">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-770'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${tab.icon} mr-3 text-lg`}></i>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Settings Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-4xl">
              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                        <input
                          type="text"
                          value={settings.companyName}
                          onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
                        <select
                          value={settings.businessType}
                          onChange={(e) => setSettings({ ...settings, businessType: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="General Contractor">General Contractor</option>
                          <option value="Subcontractor">Subcontractor</option>
                          <option value="Architect">Architect</option>
                          <option value="Engineer">Engineer</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Regional Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                        <select
                          value={settings.timezone}
                          onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                        <select
                          value={settings.dateFormat}
                          onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                        <select
                          value={settings.currency}
                          onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="USD">USD ($)</option>
                          <option value="CAD">CAD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                        <select
                          value={settings.language}
                          onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="English">English</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Project Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Settings</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Number Format
                    </label>
                    <input
                      type="text"
                      value="SBA-CUST-####-JJJ"
                      disabled
                      className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm"
                      placeholder="SBA-CUST-####-JJJ"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Format: SBA-CUST-####-JJJ where CUST = customer code (e.g., FXT, TWT, UBR), #### = master customer ID, JJJ = job number
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Example: SBA-UBR-0194-003 (Uber, customer ID 0194, third project)
                    </p>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Preferences</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Email notifications</h4>
                          <p className="text-sm text-gray-500">Receive notifications via email</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Push notifications</h4>
                          <p className="text-sm text-gray-500">Receive browser push notifications</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, pushNotifications: !settings.pushNotifications })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.pushNotifications ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">SMS notifications</h4>
                          <p className="text-sm text-gray-500">Receive text message notifications</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, smsNotifications: !settings.smsNotifications })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.smsNotifications ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.smsNotifications ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Notifications</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Weekly reports</h4>
                          <p className="text-sm text-gray-500">Receive weekly project progress reports</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, weeklyReports: !settings.weeklyReports })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.weeklyReports ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.weeklyReports ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Project updates</h4>
                          <p className="text-sm text-gray-500">Get notified when projects are updated</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, projectUpdates: !settings.projectUpdates })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.projectUpdates ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.projectUpdates ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Deadline reminders</h4>
                          <p className="text-sm text-gray-500">Receive reminders before project deadlines</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, deadlineReminders: !settings.deadlineReminders })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.deadlineReminders ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.deadlineReminders ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Authentication</h3>
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Two-factor authentication</h4>
                          <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, twoFactorAuth: !settings.twoFactorAuth })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.twoFactorAuth ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Session timeout (minutes)
                        </label>
                        <select
                          value={settings.sessionTimeout}
                          onChange={(e) => setSettings({ ...settings, sessionTimeout: e.target.value })}
                          disabled={!isEditing}
                          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="120">2 hours</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password expiry (days)
                        </label>
                        <select
                          value={settings.passwordExpiry}
                          onChange={(e) => setSettings({ ...settings, passwordExpiry: e.target.value })}
                          disabled={!isEditing}
                          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 text-sm pr-8"
                        >
                          <option value="30">30 days</option>
                          <option value="60">60 days</option>
                          <option value="90">90 days</option>
                          <option value="never">Never</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Login notifications</h4>
                          <p className="text-sm text-gray-500">Get notified of new login attempts</p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, loginNotifications: !settings.loginNotifications })}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.loginNotifications ? 'bg-blue-600' : 'bg-gray-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.loginNotifications ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Management</h3>
                    <div className="space-y-4">
                      <button
                        disabled={!isEditing}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Change Password
                      </button>
                      <button
                        disabled={!isEditing}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Download Recovery Codes
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Integration Settings */}
              {activeTab === 'integrations' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Third-party Integrations</h3>
                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-calendar-line text-blue-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Google Calendar</h4>
                            <p className="text-sm text-gray-500">Sync project deadlines and meetings</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, googleCalendar: !settings.googleCalendar })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.googleCalendar
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.googleCalendar ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-money-dollar-circle-line text-green-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">QuickBooks</h4>
                            <p className="text-sm text-gray-500">Sync financial data and invoicing</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, quickbooks: !settings.quickbooks })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.quickbooks
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.quickbooks ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-cloud-line text-blue-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Dropbox</h4>
                            <p className="text-sm text-gray-500">Store and share project files</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, dropbox: !settings.dropbox })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.dropbox
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.dropbox ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-slack-line text-purple-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Slack</h4>
                            <p className="text-sm text-gray-500">Team communication and notifications</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, slack: !settings.slack })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.slack
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.slack ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-mail-line text-blue-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Microsoft Outlook</h4>
                            <p className="text-sm text-gray-500">Email integration and calendar sync</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, outlook: !settings.outlook })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.outlook
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.outlook ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-share-line text-orange-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">SharePoint</h4>
                            <p className="text-sm text-gray-500">Document collaboration and team sites</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, sharepoint: !settings.sharepoint })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.sharepoint
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.sharepoint ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-cloud-line text-blue-600 text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">OneDrive</h4>
                            <p className="text-sm text-gray-500">Cloud storage and file synchronization</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, onedrive: !settings.onedrive })}
                          disabled={!isEditing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                            settings.onedrive
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {settings.onedrive ? 'Disconnect' : 'Connect'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">API Access</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">API Key</h4>
                          <p className="text-sm text-gray-500">Use this key to access the BuildPro API</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <code className="px-3 py-1 bg-gray-100 rounded text-sm font-mono">bp_sk_live_...</code>
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded">
                            <i className="ri-file-copy-line"></i>
                          </button>
                        </div>
                      </div>
                      <button
                        disabled={!isEditing}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Regenerate API Key
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
